// GENERAL
import React from "react";
import { motion } from "framer-motion";

// UTILS
import { getFontAwesomeIcon } from "../../../../../utils";

// COMPONENTS
import {
	BaseToolTip,
	BaseButton,
	BasePill,
	BaseText,
	StatusColorDot,
	SecondaryButton,
} from "../../../../../components";
import TertiaryButton from "../../../../../components/ui/BaseButton/TertiaryButton";
// TYPES
import { ApprovalContentHeaderProps } from "../types";

const ApprovalHeader: React.FC<ApprovalContentHeaderProps> = ({
	dateOrder,
	orderNumber,
	orderStatus,
	handleButtonClick,
	handleClose,
	toggleMenu,
	fields,
	aclPermissions,
	isApproveButtonEnabled,
}) => {
	// TEXT FROM FIELDS //
	const orderPillLabel = fields?.modalHeader?.orderBadge?.label;
	const orderPillPrefix = fields?.modalHeader?.orderBadge?.prefix;
	const viewLogButton = fields?.modalHeader?.viewLog?.label;
	const viewLogPrefix = fields?.modalHeader?.viewLog?.prefix;
	const openInNewTabButton = fields?.modalHeader?.openInNewTabButton?.label;
	const openInNewTabIcon = fields?.modalHeader?.openInNewTabButton?.prefix;
	const closeButton = fields?.modalHeader?.closeButton?.label;
	const closeIcon = fields?.modalHeader?.closeButton?.prefix;
	const orderDateLabel = fields?.modalHeader?.orderDate?.label;
	const orderNumberLabel = fields?.modalHeader?.orderNumber?.label;
	const enterPoNumberButtonLabel =
		fields?.modalHeader?.enterPoNumberButton?.label;

	// SETTINGS FROM FIELDS //
	const isViewLogButtonVisible = fields?.modalHeader?.viewLog?.isVisible;
	const isEnterPoNumberButtonVisible =
		fields?.modalHeader?.enterPoNumberButton?.isVisible;
	const approvalDecisionsOptions =
		fields?.modalHeader?.approvalDecisionsOptions;

	// CLASS NAMES //
	const parentContainerClasses = ` flex flex-col items-between justify-between sticky top-[-5px] bg-white z-20 max-lg:flex-col min-h-[62px]`;
	const lineOneContainerClasses = `flex flex-col items-start lg:flex-row lg:items-center justify-between`;
	const buttonsContainerClasses = `flex items-center lg:my-0 my-2  max-lg:px-2 max-lg:w-full max-lg:justify-between`;
	const lineTwoContainerClasses = `flex items-start max-lg:py-2`;
	return (
		<div className="pb-8 ">
			<motion.header
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				exit={{ opacity: 0 }}
				transition={{ duration: 0.2 }}
				className={parentContainerClasses}
				aria-label="approval details modal header."
				data-testid="approval-header"
			>
				<div className={lineOneContainerClasses}>
					<div
						className={"hidden max-lg:flex w-full justify-end py-2"}
					>
						{
							<>
								{isViewLogButtonVisible && (
									<SecondaryButton
										text={viewLogButton}
										dataTestId="approval-view-log-btn"
										iconClasses="pr-2"
										icon={getFontAwesomeIcon(
											viewLogPrefix,
											"regular",
										)}
										as="button"
										onClick={toggleMenu}
									/>
								)}
								<BaseToolTip
									message={openInNewTabButton}
									toolTipPlacement="top"
									children={
										<BaseButton
											borderColor="none"
											icon={getFontAwesomeIcon(
												openInNewTabIcon,
												"regular",
											)}
											onClick={() =>
												window.open(
													window.location.href,
													"_blank",
												)
											}
											as="button"
											iconClasses="text-lg"
											iconAriaLabel="Open in New Tab"
											additionalClasses="h-8 w-8 text-gray-400 hover:text-gray-700"
										/>
									}
								/>
								<BaseToolTip
									message={closeButton}
									children={
										<BaseButton
											borderColor="none"
											iconAriaLabel={closeButton}
											icon={getFontAwesomeIcon(
												closeIcon,
												"regular",
											)}
											as="button"
											onClick={handleClose}
											iconClasses="text-lg"
											additionalClasses="mr-2 h-8 w-8 text-gray-400 hover:text-gray-700"
										/>
									}
								/>
							</>
						}
					</div>
					<div className="flex items-center w-full max-sm:flex-col max-sm:items-start">
						<BasePill
							text={
								<BaseText
									size={"text-sm"}
									color={"text-navy-500"}
									text={orderPillLabel as string}
									fontFamily={"font-omnes-medium"}
									tag={"h1"}
								/>
							}
							firstIcon={getFontAwesomeIcon(
								orderPillPrefix,
								"solid",
							)}
							hoverBackgroundColor="none"
							additionalClasses="px-2.5 py-1 h-26px mr-1.5"
							shape="rounded-full"
							color="bg-supply-blue-50"
							borderColor="transparent"
							type={"span"}
							hasMobileStyle={false}
							dataTestId="approval-header-pill"
						/>
						<div className="flex items-center max-lg:py-2">
							<div className="flex items-center font-omnes-regular gap-1 max-sm:flex-col">
								<h2
									className="flex items-center text-sm mt-0"
									data-testid="approval-header-date"
								>
									<span className="text-navy-350 pr-.5 text-nowrap">
										{orderDateLabel}
									</span>
									<span className="text-navy-500 pl-1 mr-3 text-nowrap">
										{dateOrder}
									</span>
								</h2>
								<div
									className="flex flex-row gap-2 max-lg:w-full"
									data-testid="approval-header-status"
								>
									<StatusColorDot
										status={orderStatus}
										additionalClasses="flex items-center text-nowrap"
										useConvertedStatus={true}
										fontFamily="font-omnes-regular text-sm"
									/>
								</div>
							</div>
						</div>
					</div>

					<div className={buttonsContainerClasses}>
						{aclPermissions.hasApprovalsPermissions && (
							<>
								<div className="flex gap-2 mr-3 ">
									{isEnterPoNumberButtonVisible && (
										<>
											<TertiaryButton
												text={enterPoNumberButtonLabel}
												onClick={() =>
													handleButtonClick(
														"enterPoButton",
													)
												}
											/>
										</>
									)}
									{approvalDecisionsOptions
										?.filter(
											(option: any) => option.isVisible,
										)
										.map((option: any) => (
											<>
												{option.valueKey ===
												"approve" ? (
													<BaseToolTip
														message={
															isApproveButtonEnabled
																? option.label
																: option.disabledLabel
														}
														toolTipPlacement="top"
													>
														<BaseButton
															key={
																option.valueKey
															}
															dataTestId="approval-approve-btn"
															borderColor="gray-100"
															icon={getFontAwesomeIcon(
																option.prefix,
																"solid",
															)}
															iconClasses="text-base"
															additionalClasses={`p-1 h-8 w-8
																${!isApproveButtonEnabled ? "text-gray-200 hover:bg-transparent border-1 border-gray-200" : "text-mint-700 border-1 hover:bg-mint-200 hover:border-mint-700"}
															`}
															shape="rounded-md"
															onClick={() =>
																handleButtonClick(
																	option.valueKey,
																)
															}
															isDisabled={
																!isApproveButtonEnabled
															}
															disabledButtonClasses="text-gray-200 hover:bg-transparent"
														/>
													</BaseToolTip>
												) : (
													<BaseToolTip
														message={option.label}
														toolTipPlacement="top"
													>
														<BaseButton
															key={
																option.valueKey
															}
															dataTestId={`approval-${option.valueKey}-btn`}
															borderColor="gray-100"
															icon={getFontAwesomeIcon(
																option.prefix,
																"solid",
															)}
															iconClasses="text-base"
															additionalClasses={
																option.buttonClasses
															}
															shape="rounded-md"
															onClick={() =>
																handleButtonClick(
																	option.valueKey,
																)
															}
														/>
													</BaseToolTip>
												)}
											</>
										))}
								</div>
								<div className="border-l border-gray-300 h-8 max-lg:hidden"></div>
							</>
						)}

						<div className="flex items-center flex-row ml-2 gap-2 max-lg:hidden">
							{isViewLogButtonVisible && (
								<SecondaryButton
									text={viewLogButton}
									dataTestId="approval-view-log-btn"
									iconClasses="pr-2 text-primary"
									icon={getFontAwesomeIcon(
										viewLogPrefix,
										"regular",
									)}
									as="button"
									onClick={toggleMenu}
								/>
							)}
							<BaseToolTip
								message={openInNewTabButton}
								toolTipPlacement="top"
							>
								<BaseButton
									borderColor="none"
									icon={getFontAwesomeIcon(
										openInNewTabIcon,
										"regular",
									)}
									onClick={() =>
										window.open(
											window.location.href,
											"_blank",
										)
									}
									as="button"
									iconClasses="text-lg"
									additionalClasses="h-8 w-8 text-gray-400 hover:text-gray-700"
									iconAriaLabel={openInNewTabButton}
								/>
							</BaseToolTip>
							<BaseToolTip
								message={closeButton}
								toolTipPlacement="top"
							>
								<BaseButton
									dataTestId="approval-close-btn"
									borderColor="none"
									iconAriaLabel={closeButton}
									icon={getFontAwesomeIcon(
										closeIcon,
										"regular",
									)}
									as="button"
									onClick={handleClose}
									iconClasses="text-lg"
									additionalClasses="mr-2 h-8 w-8 text-gray-400 hover:text-gray-700"
								/>
							</BaseToolTip>
						</div>
					</div>
				</div>
				<div
					className={lineTwoContainerClasses}
					data-testid="approval-header-order-number"
				>
					<BaseText
						size={"text-md"}
						color={"text-navy-500"}
						text={`${orderNumberLabel} ${orderNumber}`}
						fontFamily={"font-omnes-medium"}
						tag={"h1"}
					/>
				</div>
			</motion.header>
		</div>
	);
};

export default ApprovalHeader;
